import React, { useMemo } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { Transaction } from "@common/types/transaction";
import { useLockDateState } from "@api/lock.api";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import {
    StandardModal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { getFirstUnlockedDateOnly } from "@helpers/lock";
import { useUpdateManualTransactionMutation } from "./useUpdateManualTransactionMutation";
import {
    buildUpdateManualTransactionFormSchema,
    toUpdateManualTransactionBody,
    toUpdateManualTransactionFormValues,
    UpdateManualTransactionFormValues,
} from "./updateManualTransactionForm";
import styles from "./UpdateManualTransactionModal.module.scss";

const fields = createFormikFields<UpdateManualTransactionFormValues>();

interface Props {
    show: boolean;
    transaction: Transaction;
    onClose(): void;
}

export const UpdateManualTransactionModal: React.FC<Props> = ({
    show,
    transaction,
    onClose,
}) => {
    const { lockDate, isResolved: isLockResolved } = useLockDateState(
        transaction.entityId,
    );
    const updateMutation = useUpdateManualTransactionMutation();

    const initialValues = useMemo(
        () => toUpdateManualTransactionFormValues(transaction),
        [transaction],
    );

    const formik = useFormik<UpdateManualTransactionFormValues>({
        initialValues,
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(
            buildUpdateManualTransactionFormSchema(lockDate),
        ),
        onSubmit: submitHelper({
            loading: updateMutation.isPending,
            handler: async (values) => {
                await updateMutation.mutateAsync({
                    entityId: transaction.entityId,
                    transactionId: transaction.id,
                    update: toUpdateManualTransactionBody({
                        transaction,
                        values,
                    }),
                });
            },
            onSuccess: async () => onClose(),
        }),
    });

    const hasChanges =
        Object.keys(
            toUpdateManualTransactionBody({
                transaction,
                values: formik.values,
            }),
        ).length > 0;
    const isSaving = formik.isSubmitting || updateMutation.isPending;

    return (
        <StandardModal show={show} onHide={onClose} size="md">
            <StandardModalHeader>Update transaction</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <StandardModalBody>
                        <VStack gap={16}>
                            <HStack gap={16} align="start">
                                <fields.Date
                                    name="dateOnly"
                                    label="Date"
                                    className={styles.equalWidthField}
                                    minDate={getFirstUnlockedDateOnly(lockDate)}
                                    buttonProps={{ size: "xl", block: true }}
                                />
                                <fields.Money
                                    name="amount"
                                    label="Amount"
                                    className={styles.equalWidthField}
                                    allowNegative
                                />
                            </HStack>
                            <fields.Text
                                name="bankDescription"
                                label="Bank description"
                                size="xl"
                            />
                        </VStack>
                    </StandardModalBody>
                    <StandardModalFooter>
                        <HStack gap={12} justify="end">
                            <Button
                                variant="secondary"
                                size="xl"
                                onClick={onClose}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                variant="primary"
                                type="submit"
                                size="xl"
                                loading={isSaving}
                                disabled={!hasChanges || !isLockResolved}
                            >
                                Save
                            </ButtonWithLoader>
                        </HStack>
                    </StandardModalFooter>
                </Form>
            </FormikProvider>
        </StandardModal>
    );
};
